import { get, create, update, deleteResource } from "./apiService";
import {
  resolveBusinessRoute,
  resolveBusinessUrlWithQuery,
  withBusinessContext,
} from "../utils/businessRoute";

export interface CryptoAssetRef {
  _id?: string;
  symbol?: string;
  name?: string;
  image?: string;
}

export interface CryptoChainRef {
  _id?: string;
  symbol?: string;
  name?: string;
}

export interface CryptoAddress {
  _id: string;
  firstName?: string;
  lastName?: string;
  addressName?: string;
  walletAddress?: string;
  chain?: CryptoChainRef | string | null;
  chainName?: string;
  asset?: CryptoAssetRef | string | null;
  assetName?: string;
  assetIcon?: string;
  category?: string;
  walletType?: string;
  provider?: string;
  tagNumber?: string;
  label?: string;
  address?: string;
  network?: string;
  businessId?: string | null;
  [key: string]: unknown;
}

export interface CryptoAddressListResponse {
  addresses: CryptoAddress[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateCryptoAddressPayload {
  firstName?: string;
  lastName?: string;
  addressName?: string;
  label: string;
  walletAddress?: string;
  address: string;
  asset: string;
  chain?: string;
  network?: string;
  tagNumber?: string;
  category?: string;
  walletType?: string;
  provider?: string;
}

export interface UpdateCryptoAddressPayload {
  firstName?: string;
  lastName?: string;
  addressName?: string;
  label?: string;
  walletAddress?: string;
  address?: string;
  asset?: string;
  chain?: string;
  network?: string;
  tagNumber?: string;
  category?: string;
  walletType?: string;
  provider?: string;
  memo?: string;
}

export interface PayAddress {
  _id: string;
  payId: string;
  name?: string;
  lastname?: string;
  label?: string;
  businessId?: string | null;
  [key: string]: unknown;
}

export interface PayAddressListResponse {
  addresses: PayAddress[];
  total: number;
  page: number;
  limit: number;
}

export interface CreatePayAddressPayload {
  name: string;
  lastname: string;
  payId: string;
}

export interface UpdatePayAddressPayload {
  name?: string;
  lastname?: string;
  payId?: string;
  label?: string;
}

export interface CryptoListParams {
  businessId?: string | null;
  asset?: string;
  page?: number;
  limit?: number;
}

export interface PayListParams {
  businessId?: string | null;
  page?: number;
  limit?: number;
}

const normalizeBusinessId = (
  businessId: string | null | undefined
): string | undefined => {
  if (businessId === null || businessId === undefined) {
    return undefined;
  }
  return businessId;
};

const buildListParams = (
  input: CryptoListParams | string | undefined,
  extra: Record<string, unknown> = {}
): Record<string, unknown> => {
  const params: Record<string, unknown> =
    typeof input === "string"
      ? { ...extra, asset: input }
      : { ...extra, ...(input ?? {}) };
  Object.keys(params).forEach((key) => {
    if (params[key] === undefined || params[key] === null) {
      delete params[key];
    }
  });
  return params;
};

const resolveListBusinessId = (
  paramsOrAsset: CryptoListParams | string | undefined,
  businessIdArg: string | null | undefined
): string | null | undefined => {
  if (businessIdArg !== undefined) return businessIdArg;
  if (paramsOrAsset && typeof paramsOrAsset === "object") {
    return paramsOrAsset.businessId;
  }
  return undefined;
};

export const getCryptoAddresses = async (
  paramsOrAsset?: CryptoListParams | string,
  businessId?: string | null
): Promise<CryptoAddress[]> => {
  const effectiveBusinessId = resolveListBusinessId(paramsOrAsset, businessId);
  const params = buildListParams(paramsOrAsset);
  delete params.businessId;
  const response = await get<CryptoAddressListResponse | CryptoAddress[]>(
    resolveBusinessRoute(effectiveBusinessId, "/api/v2/address-book/crypto"),
    withBusinessContext(effectiveBusinessId, params)
  );
  if (Array.isArray(response)) return response;
  return response?.addresses ?? [];
};

export const getCryptoAddressesPaged = async (
  paramsOrAsset?: CryptoListParams | string,
  businessId?: string | null
): Promise<CryptoAddressListResponse> => {
  const effectiveBusinessId = resolveListBusinessId(paramsOrAsset, businessId);
  const params = buildListParams(paramsOrAsset);
  delete params.businessId;
  const response = await get<CryptoAddressListResponse | CryptoAddress[]>(
    resolveBusinessRoute(effectiveBusinessId, "/api/v2/address-book/crypto"),
    withBusinessContext(effectiveBusinessId, params)
  );
  if (Array.isArray(response)) {
    return {
      addresses: response,
      total: response.length,
      page: 1,
      limit: response.length,
    };
  }
  return (
    response ?? {
      addresses: [],
      total: 0,
      page: 1,
      limit: 0,
    }
  );
};

export const getCryptoAddress = async (
  id: string,
  businessId?: string | null
): Promise<CryptoAddress> => {
  return get<CryptoAddress>(
    resolveBusinessRoute(businessId, `/api/v2/address-book/crypto/${id}`),
    withBusinessContext(businessId)
  );
};

export const createCryptoAddress = async (
  data: CreateCryptoAddressPayload,
  businessId?: string | null
): Promise<CryptoAddress> => {
  return create<CryptoAddress>(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/address-book/crypto"),
    data
  );
};

export const updateCryptoAddress = async (
  id: string,
  data: UpdateCryptoAddressPayload,
  businessId?: string | null
): Promise<CryptoAddress> => {
  return update<CryptoAddress>(
    resolveBusinessRoute(businessId, `/api/v2/address-book/crypto/${id}`),
    withBusinessContext(businessId),
    data
  );
};

export const deleteCryptoAddress = async (
  id: string,
  businessId?: string | null
): Promise<unknown> => {
  return deleteResource(
    resolveBusinessRoute(businessId, `/api/v2/address-book/crypto/${id}`),
    withBusinessContext(businessId)
  );
};

const PAY_ADDRESSES_TTL_MS = 3000;

const PERSONAL_PAY_CACHE_KEY = "__personal__";

const inFlightPayAddressesByContext = new Map<string, Promise<PayAddress[]>>();
const cachedPayAddressesByContext = new Map<
  string,
  { value: PayAddress[]; expiresAt: number }
>();

const payCacheKeyFor = (businessId: string | null | undefined): string => {
  const normalized = normalizeBusinessId(businessId);
  return normalized === undefined ? PERSONAL_PAY_CACHE_KEY : `b:${normalized}`;
};

const invalidatePayAddressesCache = (
  businessId?: string | null | undefined
): void => {
  if (businessId === undefined) {
    cachedPayAddressesByContext.clear();
    inFlightPayAddressesByContext.clear();
    return;
  }
  const key = payCacheKeyFor(businessId);
  cachedPayAddressesByContext.delete(key);
  inFlightPayAddressesByContext.delete(key);
};

export const __resetAddressBookCachesForTests = (): void => {
  cachedPayAddressesByContext.clear();
  inFlightPayAddressesByContext.clear();
};

export const getPayAddresses = async (
  paramsOrBusinessId?: PayListParams | string | null
): Promise<PayAddress[]> => {
  const params: PayListParams =
    paramsOrBusinessId && typeof paramsOrBusinessId === "object"
      ? paramsOrBusinessId
      : { businessId: paramsOrBusinessId as string | null | undefined };
  const cacheKey = payCacheKeyFor(params.businessId);
  const now = Date.now();

  const cached = cachedPayAddressesByContext.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return cached.value;
  }

  const existing = inFlightPayAddressesByContext.get(cacheKey);
  if (existing) {
    return existing;
  }

  const baseQuery = buildListParams(undefined, {
    ...(params.page != null ? { page: params.page } : {}),
    ...(params.limit != null ? { limit: params.limit } : {}),
  });
  const query = withBusinessContext(params.businessId, baseQuery);
  const payUrl = resolveBusinessRoute(
    params.businessId,
    "/api/v2/address-book/pay"
  );

  let pending!: Promise<PayAddress[]>;
  pending = (async () => {
    const response =
      Object.keys(query).length > 0
        ? await get<PayAddressListResponse | PayAddress[]>(payUrl, query)
        : await get<PayAddressListResponse | PayAddress[]>(payUrl);
    const value = Array.isArray(response)
      ? response
      : (response?.addresses ?? []);
    if (inFlightPayAddressesByContext.get(cacheKey) === pending) {
      cachedPayAddressesByContext.set(cacheKey, {
        value,
        expiresAt: Date.now() + PAY_ADDRESSES_TTL_MS,
      });
    }
    return value;
  })().finally(() => {
    if (inFlightPayAddressesByContext.get(cacheKey) === pending) {
      inFlightPayAddressesByContext.delete(cacheKey);
    }
  });

  pending.catch(() => {
    const existingCache = cachedPayAddressesByContext.get(cacheKey);
    if (existingCache && existingCache.expiresAt <= now) {
      cachedPayAddressesByContext.delete(cacheKey);
    }
  });

  inFlightPayAddressesByContext.set(cacheKey, pending);
  return pending;
};

export const createPayAddress = async (
  data: CreatePayAddressPayload,
  businessId?: string | null
): Promise<PayAddress> => {
  const result = await create<PayAddress>(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/address-book/pay"),
    data
  );
  invalidatePayAddressesCache(businessId);
  return result;
};

export const updatePayAddress = async (
  id: string,
  data: UpdatePayAddressPayload,
  businessId?: string | null
): Promise<PayAddress> => {
  const result = await update<PayAddress>(
    resolveBusinessRoute(businessId, `/api/v2/address-book/pay/${id}`),
    withBusinessContext(businessId),
    data
  );
  invalidatePayAddressesCache(businessId);
  return result;
};

export const deletePayAddress = async (
  id: string,
  businessId?: string | null
): Promise<unknown> => {
  const result = await deleteResource(
    resolveBusinessRoute(businessId, `/api/v2/address-book/pay/${id}`),
    withBusinessContext(businessId)
  );
  invalidatePayAddressesCache(businessId);
  return result;
};

export const getAddresses = getCryptoAddresses;

export const createWalletAddress = async (
  address: {
    firstName?: string;
    lastName?: string;
    addressName?: string;
    walletAddress: string;
    asset: string;
    assetName?: string;
    chain?: string;
    chainName?: string;
    network?: string;
    tagNumber?: string;
    walletType?: string;
    category?: string;
    provider?: string;
  },
  businessId?: string | null
): Promise<CryptoAddress> => {
  return createCryptoAddress(
    {
      label:
        address.addressName ||
        `${address.firstName ?? ""} ${address.lastName ?? ""}`.trim(),
      address: address.walletAddress,
      asset: address.asset,
      network: address.network ?? address.chain,
      firstName: address.firstName,
      lastName: address.lastName,
      addressName: address.addressName,
      walletAddress: address.walletAddress,
      chain: address.chain,
      tagNumber: address.tagNumber,
      category: address.category,
      walletType: address.walletType,
      provider: address.provider,
    },
    businessId
  );
};

export const deleteWalletAddress = deleteCryptoAddress;

export const updateWalletAddress = async (
  address: {
    id: string;
    firstName?: string;
    lastName?: string;
    addressName?: string;
    walletAddress?: string;
    asset?: string;
    chain?: string;
    network?: string;
    memo?: string;
    [key: string]: unknown;
  },
  businessId?: string | null
): Promise<CryptoAddress> => {
  return updateCryptoAddress(
    address.id,
    {
      label: address.addressName,
      address: address.walletAddress,
      network: address.network ?? address.chain,
      ...(address.memo !== undefined ? { memo: address.memo } : {}),
    },
    businessId
  );
};

export const addPayAddress = async (
  address: {
    name: string;
    lastname: string;
    payId: string;
  },
  businessId?: string | null
): Promise<PayAddress> => {
  return createPayAddress(
    {
      name: address.name,
      lastname: address.lastname,
      payId: address.payId,
    },
    businessId
  );
};
